import { NativeNavigation } from "@eventr/native/navigation";
import { useNativeNavigationViewContext } from "@eventr/native/navigation/react";
import { useNavigate } from "react-router-dom";
import { isModalRoot } from "feat/nativeNavigation/modalRoot";
import { isEntrySheetStackId, isTrailingStackId } from "feat/nativeNavigation/split.store";
import { useViewedUserId } from "feat/viewedUser/viewedUser";
import { isChangingLanguage } from "./navigationGuards";
import { withViewedUser } from "feat/viewedUser/viewedUserNavigation";
import { performPush, TRAILING_PANE_TRANSITION, type PushPathOptions, type PushPathState } from "./performPush";
import { performReplace } from "./performReplace";
import { queueViewTransition } from "./viewTransitionQueue";
import { usePaneSide } from "feat/layout/panes/pane-context";
import {
  backClosesSheet,
  backClosesTrailingEntry,
  confirmLeavingTrailingPane,
  leadingBackSteps,
  paneBackPath,
} from "feat/layout/panes/pane-navigation";

export const useGlobalRouter = () => {
  const nav = useNavigate();
  const viewedUserId = useViewedUserId();

  const paneSide = usePaneSide();
  // Each native screen is its own web view; its stack says which split column it is in.
  const nativeView = useNativeNavigationViewContext();
  const nativeStackId = nativeView.stack;

  const pushPath = async (path: string, options?: PushPathOptions) => {
    const paneOptions =
      paneSide || nativeStackId
        ? { ...options, ...(paneSide && { paneSide }), ...(nativeStackId && { nativeStackId }) }
        : options;
    await performPush(nav, path, withViewedUser(path, paneOptions, viewedUserId));
  };

  /** `transition` names a View Transition type to run in place of the slide. */
  const replacePath = async (path: string, state?: PushPathState, transition?: string) => {
    await performReplace(nav, path, state, false, transition);
  };

  const goBack = async (useRight?: boolean) => {
    if (isChangingLanguage()) return;
    const animOption = useRight ? "right" : "left";
    if (paneSide === "leading" && !(await confirmLeavingTrailingPane())) return;

    // A chat opened away from Chat is a sheet the router did not present, so the router does not
    // know to close it when there is nothing left to pop.
    if (isEntrySheetStackId(nativeStackId) && isModalRoot(nativeView.state)) {
      await NativeNavigation.dismiss({ id: nativeStackId }).catch(() => {});
      return;
    }

    // From the first screen of a trailing split column there is nothing to pop, so Back or Close
    // there closes the entry, as the web trailing pane's Close does.
    if (isTrailingStackId(nativeStackId)) {
      const { goBackInTrailingColumn } = await import("feat/nativeNavigation/NativeRootController");
      await goBackInTrailingColumn(nativeStackId).catch(() => {});
      return;
    }

    // Leaving the trailing pane's first entry shows the tab's own content there again, in place,
    // as the entry appeared; only Back within the pane slides.
    const paneBack = paneBackPath(paneSide);
    if (paneBack) {
      nav(paneBack, { replace: true });
      return;
    }

    // A sheet goes as it came, in place, and so does the trailing pane's first entry.
    if (backClosesSheet() || backClosesTrailingEntry(paneSide)) {
      nav(-1);
      return;
    }

    const steps = leadingBackSteps(paneSide) ?? 1;
    await queueViewTransition(async () => {
      nav(-steps);
    }, ["slide", animOption, ...(paneSide === "trailing" ? [TRAILING_PANE_TRANSITION] : [])]);
  };

  return { pushPath, replacePath, goBack };
};
